import { createReducer } from '@reduxjs/toolkit'

import { TKeyMetricApi, TKeyMetricRawData } from 'types/GlobalKeyMetric'

import { arrayToObject } from 'utils/functions'
import { getKeyMetricDataKey } from 'utils/metric'

import * as keyMetricActions from '../../actions/KeyMetrics/actions'
import * as authenticationActions from '../../actions/Authentication/actions'
import * as globalFilterActions from '../../actions/GlobalFilter/actions'

import {
  addChildToParent,
  removeFromTree,
  updateChildInParent
} from './utils'

export interface KeyMetricReducerType {
  data: Record<string, TKeyMetricApi>
  fetched: boolean
  isCreating: boolean
  fetching: {
    [id: string]: {
      [dataKey: string]: boolean
    }
  }
  rawData: {
    [id: string]: {
      [dataKey: string]: {
        data: TKeyMetricRawData | null
        errorStatus: number | null
      }
    }
  }
}

// Initial state
const INITIAL_STATE: KeyMetricReducerType = {
  data: {},
  fetched: false,
  isCreating: false,
  fetching: {},
  rawData: {}
}

// Reducer
const keyMetricReducer = createReducer(INITIAL_STATE, (builder) => {
  builder
    // tryGetAllKeyMetrics
    .addCase(keyMetricActions.getAllSuccess, (state, action) => {
      state.data = arrayToObject(action.payload)
      state.fetched = true
    })

    // tryCreateKeyMetric
    .addCase(keyMetricActions.create, (state) => {
      state.isCreating = true
    })
    .addCase(keyMetricActions.createSuccess, (state, action) => {
      const keyMetric = action.payload

      state.isCreating = false

      if (!keyMetric.parent) {
        state.data[keyMetric.id] = keyMetric
      } else {
        for (const key in state.data) {
          const updatedNode = addChildToParent(
            state.data[key],
            keyMetric.parent,
            keyMetric
          )

          if (updatedNode !== state.data[key]) {
            state.data[key] = updatedNode
            break
          }
        }
      }
    })
    .addCase(keyMetricActions.createFailed, (state) => {
      state.isCreating = false
    })

    // tryUpdateKeyMetric
    .addCase(keyMetricActions.updateSuccess, (state, action) => {
      const keyMetric = action.payload
      const { id, parent } = keyMetric
      // Exclude children to avoid overwriting the nested tree structure
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { children: _children, ...updates } = keyMetric

      if (parent) {
        for (const key in state.data) {
          const updatedNode = updateChildInParent(
            state.data[key],
            parent,
            id,
            updates
          )

          if (updatedNode !== state.data[key]) {
            state.data[key] = updatedNode
            break
          }
        }
      } else if (state.data[id]) {
        state.data[id] = { ...state.data[id], ...updates }
      }
    })

    // clearRawData
    .addCase(keyMetricActions.clearRawData, (state, action) => {
      delete state.rawData[action.payload]
      delete state.fetching[action.payload]
    })

    // tryDeleteKeyMetric
    .addCase(keyMetricActions.destroySuccess, (state, action) => {
      const { id } = action.payload

      if (state.data[id]) {
        delete state.data[id]
      } else {
        for (const key in state.data) {
          const updatedNode = removeFromTree(state.data[key], id)

          if (updatedNode !== state.data[key]) {
            state.data[key] = updatedNode
            break
          }
        }
      }
    })

    // tryUpdateOrderKeyMetric
    .addCase(keyMetricActions.updateOrderSuccess, (state, action) => {
      state.data = arrayToObject(action.payload)
      state.fetched = true
    })

    // tryPinKeyMetric / tryUnpinKeyMetric
    .addCase(keyMetricActions.pinSuccess, (state, action) => {
      state.data = arrayToObject(action.payload)
    })

    // tryGetKeyMetricData
    .addCase(keyMetricActions.getOneRaw, (state, action) => {
      const { id, body } = action.payload
      const dataKey = getKeyMetricDataKey(body)

      if (!state.fetching[id]) {
        state.fetching[id] = {}
      }

      state.fetching[id][dataKey] = true
    })
    .addCase(keyMetricActions.getOneRawSuccess, (state, action) => {
      const { body, data, id } = action.payload
      const dataKey = getKeyMetricDataKey(body)

      if (!state.rawData[id]) {
        state.rawData[id] = {}
      }

      state.rawData[id][dataKey] = { data, errorStatus: null }

      if (state.fetching[id]) {
        state.fetching[id][dataKey] = false
      }
    })
    .addCase(keyMetricActions.getOneRawFailed, (state, action) => {
      const { id, body, status } = action.payload
      const dataKey = getKeyMetricDataKey(body)

      if (!state.rawData[id]) {
        state.rawData[id] = {}
      }

      state.rawData[id][dataKey] = { data: null, errorStatus: status }

      if (state.fetching[id]) {
        state.fetching[id][dataKey] = false
      }
    })

    .addCase(globalFilterActions.update, (state) => {
      state.rawData = {}
      state.fetching = {}
    })

    // Sign out
    .addCase(authenticationActions.signOut, () => INITIAL_STATE)
    .addDefaultCase((state) => state)
})

export default keyMetricReducer
